<!--
 * @Author: AI Assistant
 * @Date: 2025-12-30
 * @Description: 参考音频上传组件
 * @FilePath: /weryai-web/components/Tool/ReferenceAudio.vue
-->
<template>
    <div class="mt-5">
        <!-- 标题 -->
        <label class="block text-[#A0A0A8] text-xs mb-2.5">
            {{ t('music.referenceAudioLabel') }}
        </label>
        
        <!-- 上传区域 -->
        <div
            v-if="!audioUrl"
            class="relative w-full h-[60px] bg-music-item-default border border-dashed border-[#3A3A3A] rounded-lg flex items-center justify-center cursor-pointer hover:border-[#5A5A5A] transition-colors"
            @click="triggerUpload"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
            :class="{ 'border-[#99F768]': isDragging }"
        >
            <div class="flex items-center gap-2 text-[#A0A0A8] text-sm">
                <img src="~/assets/images/common/music-upload.png" width="18" height="20" alt="upload" />
                <span>{{ t('music.clickToUploadAudio') }}</span>
            </div>
            
            <input
                ref="fileInput"
                type="file"
                accept="audio/*"
                class="hidden"
                @change="handleFileChange"
            />
        </div>
        
        <!-- 已上传音频显示 -->
        <div
            v-else
            class="relative w-full bg-[#1A1A1A] rounded-lg p-3"
        >
            <div class="flex items-center gap-3">
                <!-- 播放按钮 / 上传中状态 -->
                <button
                    v-if="!isUploading"
                    @click="togglePlay"
                    class="w-10 h-10 rounded-full bg-[#99F768] flex items-center justify-center flex-shrink-0"
                >
                    <svg v-if="!isPlaying" width="16" height="16" viewBox="0 0 24 24" fill="#000">
                        <polygon points="5 3 19 12 5 21 5 3"/>
                    </svg>
                    <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="#000">
                        <rect x="6" y="4" width="4" height="16"/>
                        <rect x="14" y="4" width="4" height="16"/>
                    </svg>
                </button>
                <div
                    v-else
                    class="w-10 h-10 rounded-full bg-[#333] flex items-center justify-center flex-shrink-0"
                >
                    <svg class="animate-spin w-5 h-5 text-[#99F768]" viewBox="0 0 24 24" fill="none">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </div>
                
                <!-- 音频信息 -->
                <div class="flex-1 min-w-0">
                    <p class="text-white text-sm truncate">{{ audioName || t('music.referenceAudioLabel') }}</p>
                    <p class="text-[#666] text-xs">
                        {{ isUploading ? t('music.uploading') : t('music.uploaded') }}
                    </p>
                </div>
                
                <!-- 删除按钮 -->
                <button
                    @click="removeAudio"
                    :disabled="isUploading"
                    class="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="2">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                </button>
            </div>
            
            <!-- 音频播放器（隐藏） -->
            <audio
                ref="audioPlayer"
                :src="audioUrl"
                @ended="isPlaying = false"
            ></audio>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { noticeMessagePopup } from '~/utils/common'
import { uploadToS3 } from '~/utils/upload'
import { useI18n } from '~/composables/useI18n'

const { t } = useI18n()

// 最大时长 5分钟（秒）
const MAX_DURATION = 5 * 60

// 上传后的 S3 URL（传给父组件用于提交接口）
const audioUrl = defineModel<string>('audioUrl', {
    default: '',
})

// 音频文件名称（传给父组件用于提交接口）
const audioName = defineModel<string>('audioName', {
    default: '',
})

// 上传状态
const isUploading = ref(false)

// UI 状态
const isDragging = ref(false)
const isPlaying = ref(false)
const fileInput = ref<HTMLInputElement>()
const audioPlayer = ref<HTMLAudioElement>()

// 触发文件选择
const triggerUpload = () => {
    fileInput.value?.click()
}

// 获取音频时长
const getAudioDuration = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
        const audio = new Audio()
        const url = URL.createObjectURL(file)
        
        // 设置只加载元数据，节省带宽
        audio.preload = 'metadata'
        
        // 超时处理（10秒）
        const timeout = setTimeout(() => {
            cleanup()
            reject(new Error('读取音频信息超时'))
        }, 10000)
        
        const cleanup = () => {
            clearTimeout(timeout)
            URL.revokeObjectURL(url)
        }
        
        audio.onloadedmetadata = () => {
            cleanup()
            const duration = audio.duration
            
            // 检查 duration 是否有效（非 Infinity、非 NaN）
            if (!isFinite(duration) || isNaN(duration)) {
                reject(new Error('无法获取有效的音频时长'))
                return
            }
            
            resolve(duration)
        }
        
        audio.onerror = () => {
            cleanup()
            reject(new Error('无法读取音频信息'))
        }
        
        audio.src = url
    })
}

// 校验文件（异步）
const validateFile = async (file: File): Promise<boolean> => {
    if (!file.type.startsWith('audio/')) {
        noticeMessagePopup(t('music.pleaseUploadAudioFile'), '', 'warning')
        return false
    }
    
    try {
        const duration = await getAudioDuration(file)
        if (duration > MAX_DURATION) {
            noticeMessagePopup(t('music.audioDurationLimit'), '', 'warning')
            return false
        }
    } catch (error) {
        noticeMessagePopup(t('music.cannotReadAudioInfo'), '', 'warning')
        return false
    }
    
    return true
}

// 处理文件选择
const handleFileChange = async (event: Event) => {
    const target = event.target as HTMLInputElement
    const file = target.files?.[0]
    if (file && await validateFile(file)) {
        // 上传到 S3
        await uploadFileToS3(file)
    }
    // 清空 input，允许重复选择同一文件
    target.value = ''
}

// 上传文件到 S3
const uploadFileToS3 = async (file: File) => {
    isUploading.value = true
    try {
        const url = await uploadToS3(file)
        audioUrl.value = url
        audioName.value = file.name  // 保存文件名
        noticeMessagePopup(t('music.audioUploadSuccess'), '', 'success')
    } catch (error: any) {
        console.error('上传失败:', error)
        noticeMessagePopup(error.message || t('music.audioUploadFailed'), '', 'error')
        // 上传失败时清除
        audioUrl.value = ''
        audioName.value = ''
    } finally {
        isUploading.value = false
    }
}

// 处理拖拽
const handleDrop = async (event: DragEvent) => {
    isDragging.value = false
    const file = event.dataTransfer?.files?.[0]
    if (file && await validateFile(file)) {
        // 上传到 S3
        await uploadFileToS3(file)
    }
}

// 播放/暂停
const togglePlay = () => {
    if (!audioPlayer.value) return
    
    if (isPlaying.value) {
        audioPlayer.value.pause()
    } else {
        audioPlayer.value.play()
    }
    isPlaying.value = !isPlaying.value
}

// 移除音频
const removeAudio = () => {
    if (audioPlayer.value) {
        audioPlayer.value.pause()
    }
    isPlaying.value = false
    audioUrl.value = ''
    audioName.value = ''
}
</script>

<style scoped>
/* 组件样式已通过 Tailwind 内联实现 */
</style>
